import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon, images } from "@core/assets";
import { LanguageSwitcher } from "@core/components";

/**
 * « Pré-auth/Barre supérieure » (Figma `521:28508`) — back to the site, and the wordmark.
 *
 * ⚠️ **Nothing else may go here.** The frame's note is explicit: no account affordance, no avatar,
 * no saved-state indicator, and *no « Se connecter »* — that door lives on the website, not in the
 * Aperçu. The emptiness of this bar is the visual form of US-E11-01's "no trace" boundary, so an
 * innocuous-looking sign-in link would quietly undo what the whole screen is for.
 *
 * The language switcher is the one addition, and it is not an account affordance: this page renders
 * outside the app shell that normally carries it, and the round's copy is localized server-side per
 * `?locale=`, so without it a visitor cannot reach the other language at all.
 */
export function PreviewTopBar() {
  return (
    <header className="flex items-center gap-xs border-b border-(--border-default) bg-surface-card px-md py-2.5 sm:px-xl">
      {/* 44×44 target at 320px, per the frame. */}
      <Link
        to="/website"
        aria-label="DZ Champions"
        className="flex size-11 shrink-0 items-center justify-center rounded-md text-(--text-secondary) outline-none hover:text-(--text-primary) focus-visible:ring-2 focus-visible:ring-focus-ring"
      >
        <Icon name="arrow-left" size={20} />
      </Link>
      {/* The mark alone at 320px, where the wordmark plus the switcher does not fit. */}
      <img
        src={images["mark-primary"]}
        alt="DZ Champions"
        className="h-7 w-auto sm:hidden"
      />
      <img
        src={images["logo-primary"]}
        alt="DZ Champions"
        className="hidden h-8 w-auto sm:block"
      />
      <div className="flex flex-1 justify-end">
        <LanguageSwitcher />
      </div>
    </header>
  );
}

/**
 * « Points d'entrée réservés » — the two post-pilot tools, named but not offered.
 *
 * US-E11-02 (check eligibility without transmitting anything) and US-E11-03 (take the dossier away
 * to read offline) are both deferred past the pilot by ruling D19. They are announced rather than
 * hidden because both serve the applicant this platform serves worst — the one paying for data by
 * the megabyte — and saying they are coming costs a line.
 */
export function ComingSoonTools() {
  const { t } = useTranslation("acquire");

  return (
    <section className="border-y border-(--border-default) bg-surface-card px-md py-lg sm:px-xl">
      <p className="text-xs-regular text-(--text-tertiary)">
        {t("preview.coming-soon")}
      </p>
    </section>
  );
}

/**
 * « Fin de lecture » — how to reach a person before committing an evening.
 *
 * US-E11-01 step 5: the path away must be as available as the path onward. A contact route at the
 * end of the read is part of that.
 */
export function PreviewFooter() {
  const { t } = useTranslation("acquire");

  return (
    <section className="border-y border-(--border-default) bg-surface-card px-md py-lg sm:px-xl">
      <p className="flex flex-wrap items-center gap-xs text-sm-regular text-(--text-secondary)">
        <span aria-hidden className="text-(--text-tertiary)">
          <Icon name="mail" size={18} />
        </span>
        {t("preview.contact")}
      </p>
    </section>
  );
}
